<template src="./Approvals.html"></template>

<script>
import { mapGetters } from 'vuex'
import {
  getPendingApprovals,
  getApprovalHistory,
  approveProcess,
  rejectProcess,
  getFilesByProcess
} from '../../services'
import { formatDate, formatDateUTC, getReportStatus, getReportStatusColor } from '../../utils/computeds'
import { OnlyAdmin, CanApprove, OnlyCompanyAdmin } from '../../utils/roles'
import { REPORT_STATUS } from '../../utils/constants'
import Companies from '../../components/Forms/Companies/Companies'

const MIN_SEARCH = 3

export default {
  name: 'Approvals',
  components: { Companies },
  data: () => ({
    tab: 0,
    pending: [],
    history: [],
    historyLoaded: false,
    filters: {
      companyId: null,
      evaluatedName: '',
      documentId: '',
      from: '',
      to: '',
      decision: null
    },
    decisionOptions: [
      { text: 'Aprobado', value: REPORT_STATUS.APPROVED },
      { text: 'Rechazado', value: REPORT_STATUS.REJECTED }
    ],
    footerProps: {
      itemsPerPageText: 'Filas por página',
      itemsPerPageAllText: 'Todos',
      pageText: '{0}-{1} de {2}'
    },
    pendingHeaders: [
      { text: 'Proceso', value: 'proceso', sortable: false },
      { text: 'Evaluado', value: 'evaluated', sortable: false },
      { text: 'Documento', value: 'documentId', sortable: false },
      { text: 'Cliente', value: 'company', sortable: false },
      { text: 'Reporte', value: 'report', sortable: false },
      { text: 'Acciones', value: 'actions', sortable: false, align: 'end' }
    ],
    historyHeaders: [
      { text: 'Fecha', value: 'decidedAt', sortable: false },
      { text: 'Proceso', value: 'proceso', sortable: false },
      { text: 'Evaluado', value: 'evaluated', sortable: false },
      { text: 'Documento', value: 'documentId', sortable: false },
      { text: 'Decisión', value: 'decision', sortable: false },
      { text: 'Aprobador', value: 'approver', sortable: false },
      { text: 'Comentarios', value: 'comments', sortable: false }
    ],
    dialog: false,
    dialogType: null,
    selected: null,
    comments: '',
    commentError: '',
    saving: false,
    filesDialog: false,
    filesSelected: null,
    filesList: [],
    filesLoading: false
  }),
  computed: {
    OnlyAdmin,
    OnlyCompanyAdmin,
    getReportStatus,
    getReportStatusColor,
    formatDate,
    formatDateUTC,
    ...mapGetters({ user: 'getUser' }),
    canDecide() {
      return CanApprove()(this.user.rol)
    }
  },
  created() {
    if (this.OnlyCompanyAdmin(this.user.rol) && !(this.user.company && this.user.company.hasClientApproval)) {
      this.$router.replace('/')
      return
    }
    this.fetchPending()
  },
  methods: {
    onTabChange(tab) {
      if (tab === 1 && !this.historyLoaded) this.fetchHistory()
    },
    async fetchPending() {
      try {
        this.pending = await getPendingApprovals({})
      } catch (err) {
        console.error('fetch pending approvals:', err)
      }
    },
    buildParams() {
      const params = {}
      if (this.filters.companyId) params.companyId = this.filters.companyId
      if (this.filters.decision) params.decision = this.filters.decision
      if (this.filters.from) params.from = this.filters.from
      if (this.filters.to) params.to = this.filters.to
      if (this.filters.evaluatedName && this.filters.evaluatedName.length >= MIN_SEARCH) {
        params.evaluatedName = this.filters.evaluatedName
      }
      if (this.filters.documentId && this.filters.documentId.length >= MIN_SEARCH) {
        params.documentId = this.filters.documentId
      }
      return params
    },
    async fetchHistory() {
      try {
        this.history = await getApprovalHistory(this.buildParams())
        this.historyLoaded = true
      } catch (err) {
        console.error('fetch approval history:', err)
      }
    },
    async openFiles(item) {
      this.filesSelected = item
      this.filesList = []
      this.filesDialog = true
      this.filesLoading = true
      try {
        this.filesList = await getFilesByProcess(item.id)
      } catch (err) {
        console.error('fetch process files:', err)
      } finally {
        this.filesLoading = false
      }
    },
    closeFiles() {
      this.filesDialog = false
      this.filesSelected = null
      this.filesList = []
    },
    openDialog(item, type) {
      this.selected = item
      this.dialogType = type
      this.comments = ''
      this.commentError = ''
      this.dialog = true
    },
    closeDialog() {
      this.dialog = false
      this.selected = null
      this.dialogType = null
      this.comments = ''
      this.commentError = ''
    },
    async confirmDecision() {
      if (!this.comments || !this.comments.trim()) {
        this.commentError = 'El comentario es obligatorio.'
        return
      }
      this.saving = true
      try {
        if (this.dialogType === 'approve') {
          await approveProcess(this.selected.id, this.comments.trim())
        } else {
          await rejectProcess(this.selected.id, this.comments.trim())
        }
        this.$swal({
          text: this.dialogType === 'approve' ? 'Reporte aprobado.' : 'Reporte rechazado.',
          buttons: false,
          icon: 'success',
          timer: 3000
        })
        this.closeDialog()
        await this.fetchPending()
        if (this.historyLoaded) await this.fetchHistory()
      } catch (err) {
        console.error('decide approval:', err)
        this.$swal({
          text: err.message ?? 'Ocurrio un error, intenta mas tarde.',
          buttons: false,
          icon: 'error',
          timer: 3000
        })
      } finally {
        this.saving = false
      }
    },
    initials(item) {
      const name = item.evaluated && item.evaluated.fullName ? item.evaluated.fullName : '?'
      return name.split(' ').filter(Boolean).slice(0, 2).map((n) => n[0]).join('').toUpperCase()
    },
    getSubmittedAt(item) {
      const date = item.metadata && item.metadata.clientApproval && item.metadata.clientApproval.submittedAt
      return date ? this.formatDateUTC(date, 'DD/MM/YYYY') : ''
    },
    getDecidedAt(item) {
      const date = item.metadata && item.metadata.clientApproval && item.metadata.clientApproval.decidedAt
      return date ? this.formatDateUTC(date, 'DD/MM/YYYY hh:mm a') : ''
    },
    getApprover(item) {
      const ca = item.metadata && item.metadata.clientApproval
      return ca && ca.decidedBy ? ca.decidedBy.name : ''
    },
    getComments(item) {
      const ca = item.metadata && item.metadata.clientApproval
      return ca && ca.comments ? ca.comments : ''
    }
  }
}
</script>
